import { NOTIFICATION_CENTER_TAB_KEYS } from "../../../redux/features/notification-center/notificationCenterConstants";

/** Manager role ids (server enum). Access to tables is driven by API, not this list. */
export const MANAGER_ROLE_IDS = Object.freeze({
  HEAD_OF_REPORTING: 11,
  REPORTING_SPECIALIST: 12,
  HEAD_OF_REGISTRATION: 13,
  REGISTRATION_SPECIALIST: 14,
  SUPPORT: 15,
  SALES: 16,
});

const KNOWN_TAB_ID_SET = new Set(NOTIFICATION_CENTER_TAB_KEYS);
const VALID_MANAGER_ROLE_IDS = new Set(Object.values(MANAGER_ROLE_IDS));

/** Tables/endpoints that accept show_all when visibility filter is Show all. */
export const NOTIFICATION_CENTER_SHOW_ALL_TABLE_IDS = NOTIFICATION_CENTER_TAB_KEYS;

/** Backend supports visibility (show_all) query params. */
export const NOTIFICATION_CENTER_API_VISIBILITY_FILTER_ENABLED = true;

/**
 * @param {{ visibilityFilter?: string }} params
 */
export function resolveNotificationCenterShowAllForApi({ visibilityFilter } = {}) {
  if (!NOTIFICATION_CENTER_API_VISIBILITY_FILTER_ENABLED) {
    return false;
  }
  return visibilityFilter === "all";
}

/**
 * @param {boolean} [showAll]
 * @param {string} [tableId] — omit for counters endpoint
 */
export function getNotificationCenterShowAllQueryParams({ showAll, tableId } = {}) {
  if (!showAll) {
    return {};
  }
  if (tableId == null || tableId === "") {
    return { show_all: true };
  }
  if (NOTIFICATION_CENTER_SHOW_ALL_TABLE_IDS.includes(tableId)) {
    return { show_all: true };
  }
  return {};
}

export function isHeadOfReportingTeam(user) {
  return getNotificationCenterManagerRoleIds(user).includes(
    MANAGER_ROLE_IDS.HEAD_OF_REPORTING
  );
}

export function isHeadOfRegistrationTeam(user) {
  return getNotificationCenterManagerRoleIds(user).includes(
    MANAGER_ROLE_IDS.HEAD_OF_REGISTRATION
  );
}

/** Head of Reporting or Head of Registration — may use show_all filter. */
export function canUseNotificationCenterShowAllFilter(user) {
  return isHeadOfReportingTeam(user) || isHeadOfRegistrationTeam(user);
}

/**
 * Notification center: GET /user top-level `manager_role` (11–16), single or array.
 */
export function canAccessNotificationCenter(user) {
  if (getNotificationCenterManagerRoleIds(user).length > 0) {
    return true;
  }
  // Fallback until manager_role is present on all manager sessions
  return user?.is_manager === true;
}

/**
 * @returns {number[]}
 */
export function getNotificationCenterManagerRoleIds(user) {
  return normalizeManagerRoleIds(user?.manager_role).filter((id) =>
    VALID_MANAGER_ROLE_IDS.has(id)
  );
}

/**
 * @param {number | number[] | null | undefined} managerRole
 * @returns {number[]}
 */
export function normalizeManagerRoleIds(managerRole) {
  if (managerRole == null) {
    return [];
  }
  if (Array.isArray(managerRole)) {
    return managerRole
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
  }
  const single = Number(managerRole);
  return Number.isNaN(single) ? [] : [single];
}

/**
 * Table ids with a positive counter in by_table.
 * @param {Record<string, number>} byTable
 * @returns {string[]}
 */
export function collectAllowedTableIdsFromByTable(byTable) {
  if (!byTable || typeof byTable !== "object" || Array.isArray(byTable)) {
    return [];
  }

  return Object.keys(byTable).filter(
    (key) => KNOWN_TAB_ID_SET.has(key) && Number(byTable[key]) > 0
  );
}

/**
 * Table ids that have at least one row in notification_center payload.
 * @param {Record<string, Record<string, unknown[]>>} dataByTab
 * @returns {string[]}
 */
export function collectAllowedTableIdsFromDataByTab(dataByTab) {
  if (!dataByTab || typeof dataByTab !== "object" || Array.isArray(dataByTab)) {
    return [];
  }

  return Object.keys(dataByTab).filter((tabId) => {
    if (!KNOWN_TAB_ID_SET.has(tabId)) {
      return false;
    }
    const tabData = dataByTab[tabId];
    if (!tabData || typeof tabData !== "object") {
      return false;
    }

    return Object.entries(tabData).some(([key, value]) => {
      if (key === "loading" || key === "error") {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (value?.items && Array.isArray(value.items)) {
        return value.items.length > 0;
      }
      return false;
    });
  });
}

/**
 * @param {string[]} allowedTableIds
 * @param {import('../config/notificationCenterConfig').NotificationCenterTabDef[]} allTabs
 */
export function filterNotificationCenterTabsByAllowedIds(allTabs, allowedTableIds) {
  if (!Array.isArray(allTabs) || allTabs.length === 0) {
    return [];
  }
  if (!Array.isArray(allowedTableIds) || allowedTableIds.length === 0) {
    return [];
  }

  const allowed = new Set(allowedTableIds);
  return allTabs.filter((tab) => allowed.has(tab.id));
}

/**
 * Visible tabs: after counters load — only by_table with count > 0;
 * before counters — union of counters and rows already in payload.
 */
export function resolveAllowedNotificationTableIds({
  counters,
  dataByTab,
  countersHydrated = false,
}) {
  const fromCounters =
    counters?.allowedTableIds?.length > 0
      ? counters.allowedTableIds
      : collectAllowedTableIdsFromByTable(counters?.byTable);

  if (countersHydrated) {
    return NOTIFICATION_CENTER_TAB_KEYS.filter((tabId) => fromCounters.includes(tabId));
  }

  const fromData = collectAllowedTableIdsFromDataByTab(dataByTab);
  const merged = new Set([...fromCounters, ...fromData]);

  return NOTIFICATION_CENTER_TAB_KEYS.filter((tabId) => merged.has(tabId));
}
